import React, { useEffect } from 'react';

const SwiftAlert = ({ type = 'info', title, message, onClose }) => {
  
  // 1. TIMER LOGIC (Set to 3 Seconds / 3000ms)
  useEffect(() => {
    if (onClose) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000); // 3 seconds is the "normal" snappy speed

      return () => clearTimeout(timer);
    }
  }, [onClose]);

  if (!message && !title) return null;

  // --- CONFIGURATION ---
  const styles = {
    // UPDATED: Success is now BLUE to match your app theme
    success: {
      container: 'bg-white border-l-4 border-blue-600 text-slate-800',
      iconBg: 'bg-blue-100 text-blue-600',
      bar: 'bg-blue-600',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
        </svg>
      )
    },
    // Error stays RED (Critical to keep red for warnings)
    error: {
      container: 'bg-white border-l-4 border-red-500 text-slate-800',
      iconBg: 'bg-red-100 text-red-500',
      bar: 'bg-red-500',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clipRule="evenodd" />
        </svg>
      )
    },
    info: {
      container: 'bg-white border-l-4 border-blue-400 text-slate-800',
      iconBg: 'bg-blue-50 text-blue-500',
      bar: 'bg-blue-400',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 9a.75.75 0 00-1.5 0v2.25H9a.75.75 0 000 1.5h2.25V15a.75.75 0 001.5 0v-2.25H15a.75.75 0 000-1.5h-2.25V9z" clipRule="evenodd" />
        </svg>
      )
    }
  };

  const currentStyle = styles[type] || styles.info;

  return (
    // Fixed Top-Right
    <div className={`fixed top-6 right-6 z-50 w-full max-w-sm overflow-hidden rounded-xl shadow-xl shadow-blue-900/10 bg-white animate-[slideInRight_0.4s_ease-out] ${currentStyle.container}`}>
      
      <div className="p-4 flex items-start gap-4">
        {/* Icon */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${currentStyle.iconBg}`}>
          {currentStyle.icon}
        </div>
        
        {/* Text */}
        <div className="flex-1 pt-0.5">
          {title && <p className="font-bold text-gray-900 text-sm">{title}</p>}
          {message && <p className="text-sm text-gray-600 font-medium leading-relaxed">{message}</p>}
        </div>

        {/* Close Button */}
        {onClose && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Progress Bar (Matches 3s timer) */}
      <div className="w-full h-1 bg-gray-100">
        <div 
          className={`h-full ${currentStyle.bar} animate-[progress_3s_linear_forwards]`} 
          style={{ width: '100%' }}
        ></div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(100%); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes progress {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default SwiftAlert;